import { AreaStack as VisxAreaStack } from '@visx/shape';
import { ScaleLinear } from 'd3-scale';

import { formatPropertyValue } from '@/explore/utils/format';

import { CategoryData } from '../grouped-chart/types';
import { generateStackedChartDataByKeys } from './utils';
import { SeriesValues } from './series-values';

import { getSeriesTotalsFormat } from '../grouped-chart/utils';

import { CategoryScale } from './types';

import styles from './stack-values.module.scss';

type StackValuesProps = {
  data: CategoryData;
  keys: string[];
  valueScale: ScaleLinear<number, number>;
  categoryScale: CategoryScale;
  avoidOverflow?: boolean;
  bar?: boolean;
  showTotals: boolean;
};

export const StackValues = (props: StackValuesProps) => {
  const { data, keys, categoryScale, valueScale, avoidOverflow, bar = false, showTotals } = props;

  if (keys.length === 0) {
    return null;
  }

  const stackedData = generateStackedChartDataByKeys(data, keys);
  const filteredSeries = data.series.filter((s) => keys.includes(s.key));
  const firstSeriesShowsValues = filteredSeries.at(0)?.showValues ?? false;

  const renderTotals = () => {
    if (!showTotals) {
      return null;
    }

    const { type, format } = getSeriesTotalsFormat(data.series);

    return stackedData.items.map((item) => {
      return (
        <text
          key={`total-${item.categoryValue.formatted}`}
          className={styles.totalLabel}
          x={(categoryScale(item.categoryValue.formatted) ?? 0) + categoryScale.bandwidth() / 2}
          y={valueScale(item.stackTotal) ?? 0}
          dy={!bar && firstSeriesShowsValues ? -22 : -8}
          style={{ textAnchor: 'middle' }}>
          {formatPropertyValue(item.stackTotal, {
            type,
            format,
            style: 'compact',
          })}
        </text>
      );
    });
  };

  return (
    <>
      <VisxAreaStack
        keys={keys}
        data={stackedData.items}
        order={'reverse'}
        x={(d) => categoryScale(d.data.categoryValue.formatted) ?? 0}
        y0={(d) => valueScale(d[0])}
        y1={(d) => valueScale(d[1]) ?? valueScale.range()[0]}>
        {({ stacks }) =>
          stacks.map((stack) => {
            const series = filteredSeries.find(({ key }) => key === stack.key);
            if (series === undefined) {
              return <></>;
            }
            return series.showValues && filteredSeries.length > 1 ? (
              <SeriesValues
                key={series.key}
                series={series}
                data={stack.map((item) => ({
                  categoryValue: item.data.categoryValue,
                  cumulativeValue: item.at(bar ? 0 : 1) ?? 0,
                  value: (item.at(1) ?? 0) - (item.at(0) ?? 0),
                }))}
                categoryScale={categoryScale}
                valueScale={valueScale}
                avoidOverflow={avoidOverflow}
                bar={bar}
              />
            ) : null;
          })
        }
      </VisxAreaStack>
      {renderTotals()}
    </>
  );
};
